if (!customElements.get('product-accordion-group')) {
  customElements.define('product-accordion-group', class extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('details')];
      this.desktop = matchMedia('(min-width: 750px)');
      this.abort = new AbortController();
      this.coveredElements = new Map();
      this.animations = new Set();
      this.sheet = this.querySelector('.product-accordion-group__sheet');
      const options = { signal: this.abort.signal };
      this.items.forEach(item => {
        item.querySelector('summary').addEventListener('click', event => {
          event.preventDefault();
          this.select(this.selectedItem === item ? null : item);
        }, options);
      });
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !this.activeItem) return;
        event.preventDefault();
        const summary = this.activeItem.querySelector('summary');
        this.select(null);
        summary.focus();
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const item = event.target.closest('details');
        if (this.items.includes(item)) this.select(item);
      }, options);
      this.desktop.addEventListener('change', () => this.select(this.selectedItem, false), options);
      this.resize = new ResizeObserver(() => this.updateLayout());
      this.form = this.closest('.form__width');
      if (this.form) this.resize.observe(this.form);
      this.setAttribute('data-ready', '');
      this.select(this.items.find(item => item.open) || null, false);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.animations?.forEach(animation => animation.cancel());
      this.restoreControls();
    }
    select(item, animate = true) {
      const sheetHeight = this.sheet.getBoundingClientRect().height;
      const heights = this.items.map(candidate => candidate.getBoundingClientRect().height);
      this.animations.forEach(animation => animation.cancel());
      this.animations.clear();
      this.selectedItem = item;
      const previous = this.activeItem;
      const finish = () => {
        this.activeItem = item;
        this.items.forEach(candidate => { candidate.open = candidate === item; });
        this.toggleAttribute('data-open', Boolean(item));
        this.updateLayout();
      };
      if (!animate || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }
      if (this.desktop.matches) {
        // Keep the old content and covered controls in place until closing finishes.
        this.activeItem = item || previous;
        this.items.forEach(candidate => { candidate.open = candidate === this.activeItem; });
        this.toggleAttribute('data-open', Boolean(this.activeItem));
        this.updateLayout();
        const targetHeight = item ? this.sheet.getBoundingClientRect().height : 52;
        this.animateHeight(this.sheet, sheetHeight, targetHeight, finish);
      } else {
        this.activeItem = item;
        this.restoreControls();
        this.toggleAttribute('data-open', Boolean(item));
        this.items.forEach((candidate, index) => {
          const opening = candidate === item;
          const wasOpen = candidate.open;
          candidate.open = opening;
          const targetHeight = candidate.getBoundingClientRect().height;
          // Native details must stay open while their content slides closed.
          candidate.open = opening || wasOpen;
          this.animateHeight(candidate, heights[index], targetHeight, () => { candidate.open = opening; });
        });
      }
    }
    animateHeight(element, from, to, finish) {
      if (Math.abs(from - to) < 1) {
        finish();
        return;
      }
      const overflow = element === this.sheet ? 'visible' : 'clip';
      const animation = element.animate([
        { height: `${from}px`, overflow, boxSizing: 'border-box' },
        { height: `${to}px`, overflow, boxSizing: 'border-box' },
      ], { duration: 340, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
      this.animations.add(animation);
      animation.onfinish = () => {
        this.animations.delete(animation);
        finish();
      };
    }
    restoreControls() {
      this.coveredElements?.forEach((wasInert, element) => { element.inert = wasInert; });
      this.coveredElements?.clear();
    }
    updateLayout() {
      this.restoreControls();
      if (!this.activeItem || !this.desktop.matches || !this.form) return;
      const bounds = this.getBoundingClientRect();
      const formBounds = this.form.getBoundingClientRect();
      // Leave the title visible above the fade and anchor the panel to the original row.
      const height = Math.max(52, bounds.bottom - formBounds.top - 100);
      this.style.setProperty('--panel-height', `${height}px`);
      const panelTop = bounds.bottom - height;
      // Covered purchase controls must not remain keyboard or screen-reader targets.
      let branch = this;
      while (branch && branch !== this.form) {
        for (const sibling of branch.parentElement.children) {
          if (sibling === branch || !(sibling instanceof HTMLElement)) continue;
          const rect = sibling.getBoundingClientRect();
          if (rect.bottom > panelTop - 80 && rect.top < bounds.bottom) {
            this.coveredElements.set(sibling, sibling.inert);
            sibling.inert = true;
          }
        }
        branch = branch.parentElement;
      }
    }
  });
}
